import React, { useState, useEffect } from 'react';
import { Upload, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import axios from 'axios';

const MediaLibrary = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchMedia();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Supprimer cette image ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMedia();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}${url}`);
    alert('URL copiée !');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bibliothèque de médias</h1>
          <p className="text-gray-600 mt-1">{files.length} image(s)</p>
        </div>
        <div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <div
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors px-4 py-2 cursor-pointer ${
                uploading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-purple-600 hover:bg-purple-700'
              } text-white`}
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Upload...' : 'Uploader une image'}
            </div>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {files.map((file) => (
            <Card key={file.filename} className="group relative overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
              <div
                onClick={() => setSelectedImage(file)}
                className="aspect-square bg-gray-100"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                  alt={file.filename}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  onClick={() => copyUrl(file.url)}
                  className="bg-white text-black hover:bg-gray-200"
                >
                  Copier URL
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(file.filename)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${selectedImage.url}`}
            alt={selectedImage.filename}
            className="max-w-full max-h-full rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
